import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback, useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router';
import { errorMessage, get, patch, post } from '../../api/client';
import type { BikeModel } from '../../api/types';
import { IconAlert, IconArrowLeft, IconStar } from '../../components/icons';
import { formatDate, Spinner, Switch, useFeedback } from '../../components/ui';
import { useNavigationGuard } from '../../hooks';
import { useT } from '../../i18n';
import { ColorsTab } from './ColorsTab';
import { DocumentsTab } from './DocumentsTab';
import { GalleryTab } from './GalleryTab';
import { GeneralTab } from './GeneralTab';
import { type Section, SECTIONS } from './shared';
import { SpecialsTab } from './SpecialsTab';
import { SpecsTab } from './SpecsTab';

export function ModelEditor() {
  const { t } = useT();
  const { id } = useParams();
  const [params, setParams] = useSearchParams();
  const qc = useQueryClient();
  const { toast, confirm } = useFeedback();
  const [dirty, setDirty] = useState(false);
  const section = (params.get('seccion') as Section) ?? 'general';

  const { data: model, isPending, error } = useQuery({
    queryKey: ['model', id],
    queryFn: async () => (await get<{ model: BikeModel }>(`/admin/models/${id}`)).model,
  });

  const onSaved = useCallback(
    (m: BikeModel) => {
      qc.setQueryData(['model', id], m);
      void qc.invalidateQueries({ queryKey: ['models'] });
      void qc.invalidateQueries({ queryKey: ['overview'] });
    },
    [qc, id],
  );
  const onDirty = useCallback((d: boolean) => setDirty(d), []);
  useNavigationGuard(dirty);

  const go = async (next: Section) => {
    if (next === section) return;
    if (dirty && !(await confirm(t('Hay cambios sin guardar en esta sección. ¿Descartarlos?'), { confirmLabel: t('Descartar'), danger: true }))) return;
    setDirty(false);
    setParams(next === 'general' ? {} : { seccion: next }, { replace: true });
  };

  const quick = async (fn: () => Promise<{ model: BikeModel }>, ok: string) => {
    try {
      onSaved((await fn()).model);
      toast(ok);
    } catch (e) {
      toast(errorMessage(e), 'error');
    }
  };

  if (isPending) return <Spinner />;
  if (error || !model) return <div className="notice error">{errorMessage(error)}</div>;

  const errors = model.issues.filter((i) => i.level === 'error');
  const warnings = model.issues.filter((i) => i.level === 'warning');
  const count = (s: Section) => model.issues.filter((i) => i.section === s && i.level === 'error').length;
  const props = { model, onSaved, onDirty };

  return (
    <>
      <Link to="/motos" className="btn btn-ghost btn-sm" style={{ marginBottom: 12 }}>
        <IconArrowLeft /> {t('Motos')}
      </Link>
      <header className="page-head">
        <div>
          <p className="kicker">{t('Moto · {path}', { path: `/productos/${model.slug}/` })}</p>
          <h1 className="row" style={{ gap: 12 }}>
            {model.name}
            {model.featured && (
              <span className="badge red">
                <IconStar width={12} /> {t('Destacada')}
              </span>
            )}
          </h1>
          <p className="faint" style={{ fontSize: 13 }}>
            {t('Última edición: {date}', { date: formatDate(model.updatedAt) })}
          </p>
        </div>
        <div className="actions">
          <Switch
            checked={model.visible}
            label={model.visible ? t('Visible en el sitio') : t('Oculta')}
            onChange={(v) => quick(() => patch(`/admin/models/${model.id}`, { visible: v }), v ? t('Ahora es visible') : t('Quedó oculta'))}
          />
          {!model.featured && (
            <button className="btn" onClick={() => quick(() => post(`/admin/models/${model.id}/featured`), t('Ahora es la destacada del carrusel'))}>
              <IconStar /> {t('Destacar')}
            </button>
          )}
        </div>
      </header>

      {(errors.length > 0 || warnings.length > 0) && (
        <div className={`notice ${errors.length ? 'error' : 'warn'}`} style={{ marginBottom: 18, display: 'block' }}>
          <strong style={{ display: 'block', marginBottom: 6 }}>
            {errors.length
              ? errors.length === 1
                ? t('Falta completar 1 cosa para poder publicarla visible')
                : t('Falta completar {n} cosas para poder publicarla visible', { n: errors.length })
              : t('Avisos')}
          </strong>
          <ul className="issues">
            {[...errors, ...warnings].map((i) => (
              <li key={i.message}>
                <IconAlert className="ico" style={{ color: i.level === 'error' ? 'var(--red)' : 'var(--warn)' }} />
                <button onClick={() => void go(i.section as Section)}>{i.message}</button>
              </li>
            ))}
          </ul>
        </div>
      )}

      <div className="tabs" role="tablist">
        {SECTIONS.map((s) => (
          <button key={s.id} role="tab" className="tab" aria-selected={section === s.id} onClick={() => void go(s.id)}>
            {t(s.label)}
            {count(s.id) > 0 && <span className="badge red">{count(s.id)}</span>}
          </button>
        ))}
      </div>

      {section === 'general' && <GeneralTab {...props} />}
      {section === 'ficha' && <SpecsTab {...props} />}
      {section === 'colores' && <ColorsTab {...props} />}
      {section === 'especiales' && <SpecialsTab {...props} />}
      {section === 'galeria' && <GalleryTab {...props} />}
      {section === 'documentos' && <DocumentsTab {...props} />}
    </>
  );
}
